import { CheckCircle, HelpCircle, Link, MapPin, Tag, Truck, Zap } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Tab } from '@headlessui/react';
import { Button } from '@/components/Common/button';

import * as yaml from 'js-yaml';

interface EntityExtractionPromptsFormProps {
  content: string;
  onSave: (content: string) => void;
  onClose: () => void;
}

interface PromptCategory {
  key: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
}

const PROMPT_CATEGORIES: PromptCategory[] = [
  { key: 'entity_extraction', labelKey: 'admin.configForms.entityExtractionPrompts.categories.extraction', icon: Tag },
  { key: 'entity_validation', labelKey: 'admin.configForms.entityExtractionPrompts.categories.validation', icon: CheckCircle },
  { key: 'entity_disambiguation', labelKey: 'admin.configForms.entityExtractionPrompts.categories.disambiguation', icon: HelpCircle },
  { key: 'relation_extraction', labelKey: 'admin.configForms.entityExtractionPrompts.categories.relations', icon: Link },
  { key: 'location_identification', labelKey: 'admin.configForms.entityExtractionPrompts.categories.location', icon: MapPin },
  { key: 'mobility_data_extraction', labelKey: 'admin.configForms.entityExtractionPrompts.categories.mobility', icon: Truck },
  { key: 'energy_data_extraction', labelKey: 'admin.configForms.entityExtractionPrompts.categories.energy', icon: Zap },
];

const EntityExtractionPromptsForm: React.FC<EntityExtractionPromptsFormProps> = ({
  content,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation();
  const [prompts, setPrompts] = useState<Record<string, { system: string; user: string }>>({});

  useEffect(() => {
    try {
      const parsed = yaml.load(content) as Record<string, { system: string; user: string }>;

      // Initialize with empty prompts for all categories
      const initialPrompts: Record<string, { system: string; user: string }> = {};
      PROMPT_CATEGORIES.forEach(cat => {
        initialPrompts[cat.key] = parsed[cat.key] || { system: '', user: '' };
      });

      setPrompts(initialPrompts);
    } catch (error) {
      console.error('Error parsing entity extraction prompts content:', error);
      // Initialize with empty prompts if parsing fails
      const emptyPrompts: Record<string, { system: string; user: string }> = {};
      PROMPT_CATEGORIES.forEach(cat => {
        emptyPrompts[cat.key] = { system: '', user: '' };
      });
      setPrompts(emptyPrompts);
    }
  }, [content]);

  const handleSystemChange = (category: string, value: string) => {
    setPrompts(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        system: value
      }
    }));
  };

  const handleUserChange = (category: string, value: string) => {
    setPrompts(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        user: value
      }
    }));
  };

  const handleSaveForm = () => {
    try {
      // Serialize back to YAML with literal style for multiline strings
      const yamlContent = yaml.dump(prompts, {
        lineWidth: -1,
        styles: {
          '!!str': 'literal'
        }
      });
      onSave(yamlContent);
    } catch (error) {
      console.error('Error serializing entity extraction prompts:', error);
    }
  };

  return (
    <div className="space-y-4">
      <div className="pb-4 border-b border-border">
        <p className="text-sm text-muted-foreground">
          {t('admin.configForms.entityExtractionPrompts.intro')}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {t('admin.configForms.shared.promptPairHint')}
        </p>
      </div>

      <Tab.Group>
        <Tab.List className="flex flex-wrap gap-1 rounded-xl bg-muted p-1">
          {PROMPT_CATEGORIES.map((category) => {
            const IconComponent = category.icon;
            return (
              <Tab
                key={category.key}
                className={({ selected }) =>
                  `flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium leading-5 transition-all
                  ${selected
                    ? 'bg-background text-foreground shadow'
                    : 'text-muted-foreground hover:bg-background/60 hover:text-foreground'
                  }`
                }
              >
                <IconComponent className="h-3.5 w-3.5" />
                {t(category.labelKey)}
              </Tab>
            );
          })}
        </Tab.List>

        <Tab.Panels className="mt-4">
          {PROMPT_CATEGORIES.map((category) => (
            <Tab.Panel key={category.key}>
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t('admin.configForms.shared.systemPrompt')}
                  </label>
                  <textarea
                    value={prompts[category.key]?.system || ''}
                    onChange={(e) => handleSystemChange(category.key, e.target.value)}
                    rows={10}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm resize-none font-mono"
                    placeholder={t('admin.configForms.shared.systemPromptFor', { label: t(category.labelKey) })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t('admin.configForms.shared.userPrompt')}
                  </label>
                  <textarea
                    value={prompts[category.key]?.user || ''}
                    onChange={(e) => handleUserChange(category.key, e.target.value)}
                    rows={10}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm resize-none font-mono"
                    placeholder={t('admin.configForms.shared.userPromptFor', { label: t(category.labelKey) })}
                  />
                </div>
              </div>
            </Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>

      <div className="flex gap-2 justify-end pt-4 border-t border-border">
        <Button onClick={onClose} variant="outline">
          {t('actions.cancel')}
        </Button>
        <Button onClick={handleSaveForm}>
          {t('actions.save')}
        </Button>
      </div>
    </div>
  );
};

export default EntityExtractionPromptsForm;
